import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Loader2, Check, X, Edit2 } from "lucide-react";
import { toast } from "sonner";
import { FeedbackButtons } from "@/components/agent/FeedbackButtons";
import { useAgent } from "@/contexts/AgentContext";
import { StrataLogo } from "@/components/StrataLogo";

interface Field {
  name: string;
  type: string;
  isRequired?: boolean;
  config?: {
    options?: string[];
  };
}

interface SchemaPreview {
  tableName: string;
  fields: Field[];
  records: Array<Record<string, any>>;
  confidence: number;
}

interface StructurePreviewDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schema: SchemaPreview | null;
  onAccept: (schema: SchemaPreview) => void;
  onReject: () => void;
}

const FIELD_TYPES = [
  "text", "long_text", "number", "currency", "percent",
  "single_select", "multi_select", "checkbox", "date",
  "email", "phone", "url"
];

export function StructurePreviewDrawer({
  open,
  onOpenChange,
  schema,
  onAccept,
  onReject
}: StructurePreviewDrawerProps) {
  const { logInteraction, recordFeedback } = useAgent();
  const [editedSchema, setEditedSchema] = useState<SchemaPreview | null>(schema);
  const [isEditing, setIsEditing] = useState(false);
  const [interactionId, setInteractionId] = useState<string | null>(null);

  // Log when schema is shown
  useEffect(() => {
    if (open && schema) {
      logInteraction(
        "schema_detection_shown",
        { tableName: schema.tableName, fieldCount: schema.fields.length },
        { confidence: schema.confidence },
        "success"
      ).then((id) => setInteractionId(id));
    }
  }, [open, schema]);

  if (!schema) return null;

  const currentSchema = editedSchema || schema;

  const handleFieldChange = (index: number, field: Partial<Field>) => {
    const updatedFields = [...currentSchema.fields];
    updatedFields[index] = { ...updatedFields[index], ...field };
    setEditedSchema({ ...currentSchema, fields: updatedFields });
    setIsEditing(true);
  };

  const handleRemoveField = (index: number) => {
    const updatedFields = currentSchema.fields.filter((_, i) => i !== index);
    setEditedSchema({ ...currentSchema, fields: updatedFields });
    setIsEditing(true);
  };

  const handleAddField = () => {
    const newField: Field = { name: "new_field", type: "text" };
    setEditedSchema({
      ...currentSchema,
      fields: [...currentSchema.fields, newField]
    });
    setIsEditing(true);
  };

  const handleAccept = async () => {
    if (interactionId) {
      await recordFeedback(interactionId, "positive");
    }
    onAccept(currentSchema);
    setIsEditing(false);
  };

  const handleReject = async () => {
    if (interactionId) {
      await recordFeedback(interactionId, "negative");
    }
    onReject();
  };

  const handleFeedback = async (feedback: "positive" | "negative") => {
    if (interactionId) {
      await recordFeedback(interactionId, feedback);
    }
  };

  const confidenceColor = currentSchema.confidence > 0.8 
    ? "text-green-600" 
    : currentSchema.confidence > 0.5 
    ? "text-yellow-600" 
    : "text-orange-600";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <StrataLogo size="md" />
            Structure Preview
          </SheetTitle>
          <SheetDescription>
            AI detected this structure from your content. Review and adjust before creating.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Table Name */}
          <div className="space-y-2">
            <Label htmlFor="table-name">Table Name</Label>
            <Input
              id="table-name"
              value={currentSchema.tableName}
              onChange={(e) => {
                setEditedSchema({ ...currentSchema, tableName: e.target.value });
                setIsEditing(true);
              }}
              placeholder="Enter table name"
            />
          </div>

          {/* Confidence Badge + Feedback */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">AI Confidence:</span>
              <Badge variant="outline" className={confidenceColor}>
                {Math.round(currentSchema.confidence * 100)}%
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Was this helpful?</span>
              <FeedbackButtons
                onPositive={() => handleFeedback("positive")}
                onNegative={() => handleFeedback("negative")}
              />
            </div>
          </div>

          {/* Fields */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Fields ({currentSchema.fields.length})</Label>
              <Button variant="outline" size="sm" onClick={handleAddField}>
                + Add Field
              </Button>
            </div>

            <div className="space-y-3">
              {currentSchema.fields.map((field, index) => (
                <div key={index} className="flex items-start gap-2 p-3 border rounded-lg bg-card">
                  <div className="flex-1 space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Field Name</Label>
                        <Input
                          value={field.name}
                          onChange={(e) => handleFieldChange(index, { name: e.target.value })}
                          className="h-8 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Type</Label>
                        <Select
                          value={field.type}
                          onValueChange={(value) => handleFieldChange(index, { type: value })}
                        >
                          <SelectTrigger className="h-8 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {FIELD_TYPES.map((type) => (
                              <SelectItem key={type} value={type}>
                                {type.replace(/_/g, " ")}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {field.config?.options && (
                      <div className="flex flex-wrap gap-1">
                        {field.config.options.map((opt, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">
                            {opt}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive"
                    onClick={() => handleRemoveField(index)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          {/* Sample Records Preview */}
          {currentSchema.records.length > 0 && (
            <div className="space-y-2">
              <Label>Sample Records ({currentSchema.records.length})</Label>
              <div className="max-h-40 overflow-y-auto border rounded-lg p-3 bg-muted/50">
                <pre className="text-xs">
                  {JSON.stringify(currentSchema.records.slice(0, 3), null, 2)}
                </pre>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-4 border-t">
            <Button onClick={handleAccept} className="flex-1" disabled={!currentSchema.fields.length}>
              <Check className="h-4 w-4 mr-2" />
              Accept & Create
            </Button>
            <Button onClick={handleReject} variant="outline" className="flex-1">
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
